import { useMutation } from "@apollo/client";
import pluralize from "pluralize";
import React, { useState } from "react";
import { useWebOnboardingSteps } from "../../hooks/useWebOnboardingSteps";
import { ButtonGroup, StepCounter, StepLayout } from "../../shared-components";
import { InviteSlide } from "./InviteSlide";

import { TrackingEvents, track } from "../../../../util/analytics.util";
import { BulkInviteEvent } from "../../../invite-search/types";
import { setError } from "../../utils/setError";
import { InviteStepMutationDocument } from "./__generated__/InviteStep.generated";

export const InviteStep = ({ orgId }: { orgId: string }) => {
  const { goForward, goBack, shouldShowSecondaryButton } = useWebOnboardingSteps();

  const [selectedBulkInvites, setSelectedBulkInvites] = useState<BulkInviteEvent[]>([]); // used only for tracking purposes
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<Set<string>>(new Set());
  const [updateOrgInvite] = useMutation(InviteStepMutationDocument, {
    onError: (error) => {
      setError({ message: "failed to invite teammates", error, showUserMessage: true });
      setDisableButtons(false);
      goForward();
    },
  });
  const [disableButtons, setDisableButtons] = useState(false);

  const updateBulkInviteEvents = (checked: boolean, bulkInviteEvent: BulkInviteEvent) => {
    const newSelectedBulkInvites = [...selectedBulkInvites];
    if (checked) {
      newSelectedBulkInvites.push(bulkInviteEvent);
    } else {
      const bulkInvite = newSelectedBulkInvites.find(
        (invite) => invite.title === bulkInviteEvent.title,
      );
      if (bulkInvite) {
        const index = newSelectedBulkInvites.indexOf(bulkInvite);
        newSelectedBulkInvites.splice(index, 1);
      }
    }
    setSelectedBulkInvites(newSelectedBulkInvites);
  };

  const updateSelectedUsers = (
    calendarIds: string[],
    checked: boolean,
    bulkInviteEvent?: BulkInviteEvent,
  ) => {
    const newCalendarIds = new Set([...selectedCalendarIds]);
    calendarIds.forEach((calendarId) => {
      if (checked) {
        newCalendarIds.add(calendarId);
      } else {
        newCalendarIds.delete(calendarId);
      }
    });
    setSelectedCalendarIds(newCalendarIds);

    if (bulkInviteEvent) {
      updateBulkInviteEvents(checked, bulkInviteEvent);
    }
  };

  const onSaveAndGoForward = () => {
    if (selectedCalendarIds.size > 0) {
      track(TrackingEvents.WEB_ONBOARDING.INVITE_SENT, {
        inviteCount: selectedCalendarIds.size,
        invitedCalendarIds: Array.from(selectedCalendarIds),
        bulkInvites: selectedBulkInvites,
        numSelectedBulkInvites: selectedBulkInvites.length,
      });
      setDisableButtons(true);
      const calendarIdList = Array.from(selectedCalendarIds);
      void updateOrgInvite({
        variables: {
          input: { calendarIds: calendarIdList, orgRelayId: orgId },
        },
        onCompleted: () => {
          track(TrackingEvents.WEB_ONBOARDING.INVITE_SENT_ON_COMPLETE, {
            inviteCount: selectedCalendarIds.size,
            invitedCalendarIds: Array.from(selectedCalendarIds),
            bulkInvites: selectedBulkInvites,
            numSelectedBulkInvites: selectedBulkInvites.length,
          });
          setDisableButtons(false);
          goForward();
        },
        onError: (error) => {
          setError({ error: error, message: "failed to send invites", showUserMessage: false });
          goForward();
        },
      });
    } else {
      track(TrackingEvents.WEB_ONBOARDING.INVITE_SKIPPED);
      goForward();
    }
  };

  const primaryButtonText =
    selectedCalendarIds.size > 0
      ? `Invite ${pluralize("person", selectedCalendarIds.size, true)}`
      : "Continue";

  return (
    <StepLayout>
      <StepCounter />
      <InviteSlide onCheck={updateSelectedUsers} />
      <ButtonGroup
        shouldShowSecondary={shouldShowSecondaryButton}
        primaryLabel={primaryButtonText}
        secondaryLabel={"Back"}
        onClickPrimary={onSaveAndGoForward}
        onClickSecondary={goBack}
        disabled={disableButtons}
      />
    </StepLayout>
  );
};
